/**
 * MigrationsListPage — `/admin/migrations`
 *
 * Lists all BusinessProfile migrations with their current state and
 * canary progress. Rows click through to the detail page.
 */
import { CaretRight, Warning } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card } from '../../shared/ui/Card';
import { ProgressBar } from '../../shared/ui/ProgressBar';
import { StatusBadge, type StatusVariant } from '../../shared/ui/StatusBadge';
import { cn } from '../../shared/utils/cn';
import { useProfileMigrations } from '../../features/profile-migrations/useProfileMigrations';
import type {
  BumpType,
  MigrationState,
} from '../../features/profile-migrations/types';

const STATE_VARIANT: Record<MigrationState, StatusVariant> = {
  PENDING: 'neutral',
  PRE_FLIGHT: 'info',
  READY: 'info',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  FAILED: 'danger',
  ROLLED_BACK: 'danger',
  DEFERRED: 'warning',
  MANUAL_HOLD: 'warning',
};

const BUMP_TONE: Record<BumpType, string> = {
  patch: 'bg-biz-info-subtle text-biz-info border-biz-info/30',
  minor: 'bg-biz-warning-subtle text-biz-warning border-biz-warning/30',
  major: 'bg-biz-danger-subtle text-biz-danger border-biz-danger/30',
};

export function MigrationsListPage(): JSX.Element {
  const { t } = useTranslation('adminMigrations');
  const migrationsQuery = useProfileMigrations();

  return (
    <div className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto">
      <header className="mb-biz-section">
        <h1 className="text-biz-3xl biz-heading font-semibold mb-biz-content">
          {t('list.title')}
        </h1>
        <p className="text-biz-base text-biz-muted-foreground">{t('list.subtitle')}</p>
      </header>

      {migrationsQuery.isLoading ? (
        <LoadingSkeleton />
      ) : migrationsQuery.isError ? (
        <div
          role="alert"
          className="flex items-start gap-biz-content-lg px-biz-content-lg py-biz-content-lg rounded-biz-card bg-biz-danger-subtle text-biz-danger border border-biz-danger/30"
        >
          <Warning weight="regular" size={20} aria-hidden="true" className="shrink-0" />
          <p className="text-biz-sm">{t('list.loadError')}</p>
        </div>
      ) : (migrationsQuery.data?.length ?? 0) === 0 ? (
        <Card className="px-biz-card-lg py-biz-section text-center text-biz-muted-foreground">
          {t('list.empty')}
        </Card>
      ) : (
        <Card className="divide-y divide-biz-border">
          {migrationsQuery.data!.map((m) => {
            const pct =
              m.stats.eligible > 0
                ? Math.round((m.stats.migrated / m.stats.eligible) * 100)
                : 0;
            return (
              <Link
                key={m.id}
                to={`/admin/migrations/${m.id}`}
                className={cn(
                  'grid gap-biz-content-lg',
                  'grid-cols-1 md:grid-cols-[1fr_auto_auto_auto]',
                  'px-biz-card py-biz-content-lg',
                  'min-h-[96px] hover:bg-biz-muted',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-inset',
                  'transition-colors duration-biz-fast',
                )}
                aria-label={`${m.subVerticalDisplayName} ${m.fromVersion} → ${m.toVersion}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-biz-content flex-wrap">
                    <h2 className="text-biz-base font-semibold truncate">
                      {m.subVerticalDisplayName}
                    </h2>
                    <span className="font-mono text-biz-xs text-biz-muted-foreground">
                      v{m.fromVersion} → v{m.toVersion}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-biz-content flex-wrap text-biz-xs text-biz-muted-foreground">
                    <span
                      className={cn(
                        'inline-flex items-center px-biz-content py-0.5 rounded-biz-full border text-biz-xs font-semibold uppercase tracking-wider',
                        BUMP_TONE[m.bumpType],
                      )}
                    >
                      {t(`bumpType.${m.bumpType}` as const)}
                    </span>
                    <span aria-hidden="true">·</span>
                    <span>{t(`migrationClass.${m.migrationClass}` as const)}</span>
                    {m.paused ? (
                      <>
                        <span aria-hidden="true">·</span>
                        <span className="text-biz-warning font-medium">
                          {t('list.paused')}
                        </span>
                      </>
                    ) : null}
                  </div>
                  <div className="mt-biz-content max-w-md">
                    <ProgressBar
                      value={pct}
                      tone={
                        m.state === 'FAILED' || m.state === 'ROLLED_BACK'
                          ? 'danger'
                          : m.state === 'COMPLETED'
                            ? 'success'
                            : 'primary'
                      }
                      showValue
                      label={t('list.progressLabel', {
                        migrated: m.stats.migrated,
                        eligible: m.stats.eligible,
                      })}
                    />
                  </div>
                </div>

                <div className="md:text-right text-biz-xs text-biz-muted-foreground self-center">
                  {m.canaryPercentage > 0 ? (
                    <span>{t('list.canaryGate', { value: m.canaryPercentage })}</span>
                  ) : (
                    <span>{t('list.notStarted')}</span>
                  )}
                </div>

                <StatusBadge
                  variant={STATE_VARIANT[m.state]}
                  label={t(`state.${m.state}` as const)}
                  className="self-center"
                />

                <CaretRight
                  size={20}
                  weight="regular"
                  aria-hidden="true"
                  className="shrink-0 text-biz-muted-foreground self-center hidden md:block"
                />
              </Link>
            );
          })}
        </Card>
      )}
    </div>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <Card className="divide-y divide-biz-border" aria-busy="true" aria-live="polite">
      {[0, 1, 2].map((i) => (
        <div key={i} className="px-biz-card py-biz-content-lg">
          <div className="h-6 w-1/3 bg-biz-muted rounded animate-pulse mb-2" />
          <div className="h-2 w-full bg-biz-muted rounded animate-pulse" />
        </div>
      ))}
    </Card>
  );
}
